import { create } from 'zustand'
import { apiClient } from '../lib/api'
import { track } from '../lib/analytics'
import type { Collection, CollectionItem, CreationTemplate, PaginatedResponse } from '../lib/types'

interface CreatorState {
  collections: Collection[]
  templates: CreationTemplate[]
  currentCollection: Collection | null
  loading: boolean
  error: string | null

  fetchCollections: () => Promise<void>
  createCollection: (title: string) => Promise<Collection>
  deleteCollection: (id: number) => Promise<void>
  fetchCollectionDetail: (id: number) => Promise<void>
  addItem: (collectionId: number, timelineEntryId: number, note?: string) => Promise<void>
  removeItem: (collectionId: number, itemId: number) => Promise<void>
  updateItemNote: (collectionId: number, itemId: number, note: string) => Promise<void>
  reorderItems: (collectionId: number, items: { id: number; position: number }[]) => Promise<void>
  fetchTemplates: () => Promise<void>
  generateDraft: (collectionId: number, mode: 'quick' | 'guided', templateId: number) => Promise<{ task_id?: string; thread_id?: number }>
  clearCurrent: () => void
}

export const useCreatorStore = create<CreatorState>((set, get) => ({
  collections: [],
  templates: [],
  currentCollection: null,
  loading: false,
  error: null,

  fetchCollections: async () => {
    set({ loading: true, error: null })
    try {
      const data = await apiClient.get<PaginatedResponse<Collection>>('creator/collections/')
      set({ collections: data.results })
    } catch {
      set({ error: 'Failed to load collections.' })
    } finally {
      set({ loading: false })
    }
  },

  createCollection: async (title) => {
    const collection = await apiClient.post<Collection>('creator/collections/', { title })
    set((s) => ({ collections: [collection, ...s.collections] }))
    track('collection_created', { title_length: title.length })
    return collection
  },

  deleteCollection: async (id) => {
    await apiClient.delete(`creator/collections/${id}/`)
    set((s) => ({
      collections: s.collections.filter((c) => c.id !== id),
    }))
  },

  fetchCollectionDetail: async (id) => {
    set({ loading: true, error: null })
    try {
      const collection = await apiClient.get<Collection>(`creator/collections/${id}/`)
      set({ currentCollection: collection })
    } catch {
      set({ error: 'Failed to load collection.' })
    } finally {
      set({ loading: false })
    }
  },

  addItem: async (collectionId, timelineEntryId, note) => {
    const item = await apiClient.post<CollectionItem>(
      `creator/collections/${collectionId}/items/`,
      { timeline_entry_id: timelineEntryId, note: note || '' }
    )
    set((s) => {
      if (!s.currentCollection || s.currentCollection.id !== collectionId) return s
      return {
        currentCollection: {
          ...s.currentCollection,
          item_count: s.currentCollection.item_count + 1,
          items: [...(s.currentCollection.items || []), item],
        },
      }
    })
  },

  updateItemNote: async (collectionId, itemId, note) => {
    await apiClient.patch(`creator/collections/${collectionId}/items/${itemId}/`, { note })
    set((s) => {
      if (!s.currentCollection || s.currentCollection.id !== collectionId) return s
      return {
        currentCollection: {
          ...s.currentCollection,
          items: (s.currentCollection.items || []).map((i) =>
            i.id === itemId ? { ...i, note } : i
          ),
        },
      }
    })
  },

  removeItem: async (collectionId, itemId) => {
    await apiClient.delete(`creator/collections/${collectionId}/items/${itemId}/`)
    set((s) => {
      if (!s.currentCollection || s.currentCollection.id !== collectionId) return s
      return {
        currentCollection: {
          ...s.currentCollection,
          item_count: Math.max(0, s.currentCollection.item_count - 1),
          items: (s.currentCollection.items || []).filter((i) => i.id !== itemId),
        },
      }
    })
  },

  reorderItems: async (collectionId, items) => {
    await apiClient.patch(`creator/collections/${collectionId}/items/reorder/`, { items })
    // Refetch to get canonical order
    get().fetchCollectionDetail(collectionId)
  },

  fetchTemplates: async () => {
    const data = await apiClient.get<PaginatedResponse<CreationTemplate>>('creator/templates/')
    set({ templates: data.results })
  },

  generateDraft: async (collectionId, mode, templateId) => {
    const result = await apiClient.post<{ task_id?: string; thread_id?: number; collection_status?: string }>(
      `creator/collections/${collectionId}/draft/`,
      { mode, template_id: templateId }
    )
    return result
  },

  clearCurrent: () => set({ currentCollection: null }),
}))
